import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import http from '../config/http-service';

export interface ISupplierSearchData {
  id: number;
  name: string;
  email?: string;
  landLineNumber?: string;
  isActive?: boolean;
  countryCode?: string;
  imageUrl?: string;
  minimumOrder?: number;
  hasVerifiedEmail?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ISupplierSearchResponse {
  data: ISupplierSearchData[];
  total: number;
  page: number;
  limit: number;
}

const endpoint = 'api/v1/cms/suppliers';

type TData = ISupplierSearchResponse;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { name: string }];

type Options = Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & {
  enabled?: boolean;
};
type ReturnType = UseQueryResult<TData, TError>;

/**
 * Search suppliers by name (minimum 2 characters), within the caller's permitted countries.
 */
export function useSearchSuppliersApi(searchTerm: string, options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const { enabled: userEnabled = true, ...restOptions } = options || {};

  const name = searchTerm.trim().length >= 2 ? searchTerm.trim() : '';

  const queryKey: TQueryKey = [endpoint, { name }];

  const queryFn = async (): Promise<TData> => {
    const { data } = await http.get(endpoint, {
      params: { page: 1, limit: 20, sort: 'name.asc', name: name || undefined },
    });
    return data;
  };

  return useQueryWrapper(queryKey, queryFn, {
    enabled: isLoggedIn && userEnabled && searchTerm.trim().length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    ...restOptions,
  });
}
